import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoadingTypes } from '.';
import { ICreateLoadRequest } from '../logistics';
import { ISalesCallDialogOptions } from './uiInterfaces';

interface IUiState {
  fullPageLoadingText: string;
  loading: LoadingTypes[];
  messageDialog: { title: string; messages: string[]; exception?: object } | undefined;
  showErrorPage: boolean;
  showFilterDrawer: boolean;
  showFullPageLoading: boolean;
  showInfoCallDialog: boolean;
  showPowerAssessmentDialog: { accountNum: string } | undefined;
  showSalesCallDialog: ISalesCallDialogOptions | undefined;
  spinnerDialogMessage: string;
  showSplashPage: boolean;
  showLogisticsSidebarDropItem: ICreateLoadRequest | undefined;
}

const initialState: IUiState = {
  fullPageLoadingText: 'Loading...',
  loading: [],
  messageDialog: undefined,
  showErrorPage: false,
  showFilterDrawer: true,
  showFullPageLoading: false,
  showInfoCallDialog: false,
  showLogisticsSidebarDropItem: undefined,
  showPowerAssessmentDialog: undefined,
  showSalesCallDialog: undefined,
  showSplashPage: false,
  spinnerDialogMessage: ''
};

const slice = createSlice({
  initialState,
  name: 'ui',
  reducers: {
    hideErrorPage: state => {
      state.showErrorPage = false;
    },
    hideFullPageLoading: state => {
      state.showFullPageLoading = false;
    },
    hideInfoCallDialog: state => {
      state.showInfoCallDialog = false;
    },
    hideLogisticsSidebarDropItem: state => {
      state.showLogisticsSidebarDropItem = undefined;
    },
    hideMessageDialog: state => {
      state.messageDialog = undefined;
    },
    hidePowerAssessmentDialog: state => {
      state.showPowerAssessmentDialog = undefined;
    },
    hideSalesCallDialog: state => {
      state.showSalesCallDialog = undefined;
    },
    hideSpinnerDialog: state => {
      state.spinnerDialogMessage = '';
    },
    hideSplashPage: state => {
      state.showSplashPage = false;
    },
    showErrorPage: state => {
      state.showErrorPage = true;
    },
    showFullPageLoading: {
      prepare: (text: string = 'loading') => ({ payload: { text } }),
      reducer: (state, action: PayloadAction<{ text: string }>) => {
        state.fullPageLoadingText = action.payload.text;
        state.showFullPageLoading = true;
      }
    },
    showInfoCallDialog: state => {
      state.showInfoCallDialog = true;
    },
    showLogisticsSidebarDropItem: (state, action: PayloadAction<ICreateLoadRequest>) => {
      state.showLogisticsSidebarDropItem = action.payload;
    },
    showMessageDialog: (state, action: PayloadAction<{ messages: string[]; title: string; exception?: object }>) => {
      state.messageDialog = action.payload;
    },
    showPowerAssessmentDialog: (state, action: PayloadAction<{ accountNum: string }>) => {
      state.showPowerAssessmentDialog = action.payload;
    },
    showSalesCallDialog: (state, action: PayloadAction<ISalesCallDialogOptions>) => {
      state.showSalesCallDialog = action.payload;
    },
    showSpinnerDialog: {
      prepare: (message: string = `You're action is being processed...`) => ({ payload: message }),
      reducer: (state, action: PayloadAction<string>) => {
        state.spinnerDialogMessage = action.payload;
      }
    },
    showSplashPage: state => {
      state.showSplashPage = true;
    },
    toggleFilterDrawer: state => {
      state.showFilterDrawer = !state.showFilterDrawer;
    },
    toggleLoading: (state, action: PayloadAction<LoadingTypes>) => {
      state.loading = state.loading.includes(action.payload)
        ? state.loading.filter(x => x !== action.payload)
        : [...state.loading, action.payload];
    }
  }
});

export const { actions: uiActionCreators, reducer: uiReducer } = slice;
